import { useEffect, useState } from "react";
import api, { formatEuro, imageUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CookingPot, BowlFood, Package } from "@phosphor-icons/react";

export default function MenuOverview() {
  const [dishes, setDishes] = useState([]);
  const [bowls, setBowls] = useState([]);
  const [combos, setCombos] = useState([]);

  useEffect(() => {
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
    api.get("/bowls").then((r) => setBowls(r.data)).catch(() => {});
    api.get("/combos").then((r) => setCombos(r.data)).catch(() => {});
  }, []);

  const categorie = [...new Set(dishes.map((d) => d.categoria))];

  return (
    <div className="space-y-6 fade-in" data-testid="menu-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold">Menu</h1>
        <p className="text-[#6B7280] mt-1">Panoramica completa dell'offerta RisoRice con prezzi e valori nutrizionali.</p>
      </div>

      <Tabs defaultValue="piatti">
        <TabsList className="h-auto bg-white border border-[#E5E5E0] rounded-full p-1">
          <TabsTrigger value="piatti" data-testid="menu-tab-piatti" className="rounded-full px-6 py-2 gap-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white"><CookingPot size={16} /> Piatti</TabsTrigger>
          <TabsTrigger value="bowl" data-testid="menu-tab-bowl" className="rounded-full px-6 py-2 gap-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white"><BowlFood size={16} /> Bowl</TabsTrigger>
          <TabsTrigger value="combo" data-testid="menu-tab-combo" className="rounded-full px-6 py-2 gap-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white"><Package size={16} /> Combo</TabsTrigger>
        </TabsList>

        <TabsContent value="piatti" className="mt-6 space-y-8">
          {categorie.map((cat) => (
            <div key={cat}>
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">{cat} <span className="text-sm font-normal text-[#6B7280]">({dishes.filter((d) => d.categoria === cat).length})</span></h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dishes.filter((d) => d.categoria === cat).map((d) => (
                  <div key={d.id} className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden" data-testid={`menu-dish-${d.id}`}>
                    {d.foto && <img src={imageUrl(d.foto)} alt={d.nome} className="h-36 w-full object-cover" />}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-bold">{d.nome}</p>
                        <p className="font-extrabold text-[#D95D39]" style={{ fontFamily: "Manrope" }}>{formatEuro(d.prezzo)}</p>
                      </div>
                      <p className="text-sm text-[#6B7280] mb-4 min-h-[40px]">{d.descrizione}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="rounded-full text-xs">{d.nutrizione.kcal} kcal</Badge>
                        <Badge variant="outline" className="rounded-full text-xs">P {d.nutrizione.proteine}g</Badge>
                        <Badge variant="outline" className="rounded-full text-xs">C {d.nutrizione.carboidrati}g</Badge>
                        <Badge variant="outline" className="rounded-full text-xs">G {d.nutrizione.grassi}g</Badge>
                        {!d.attivo && <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-100 rounded-full text-xs">Non attivo</Badge>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="bowl" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {bowls.map((b) => (
              <div key={b.id} className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden" data-testid={`menu-bowl-${b.id}`}>
                {b.foto && <img src={imageUrl(b.foto)} alt={b.nome} className="h-40 w-full object-cover" />}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-bold">{b.nome}</p>
                    <p className="font-extrabold text-[#D95D39]" style={{ fontFamily: "Manrope" }}>da {formatEuro(b.prezzo_base)}</p>
                  </div>
                  <p className="text-sm text-[#6B7280] mb-4">{b.descrizione}</p>
                  <div className="space-y-2">
                    {b.sezioni.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="font-semibold w-24">{s.nome}</span>
                        <span className="text-[#6B7280] text-xs">{s.opzioni.map((o) => o.nome).join(", ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="combo" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {combos.map((c) => (
              <div key={c.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid={`menu-combo-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <p className="font-bold">{c.nome}</p>
                  <div className="text-right">
                    <p className="text-xs text-[#6B7280] line-through">{formatEuro(c.prezzo_originale)}</p>
                    <p className="font-extrabold text-[#D95D39]" style={{ fontFamily: "Manrope" }}>{formatEuro(c.prezzo_combo)}</p>
                  </div>
                </div>
                <p className="text-sm text-[#6B7280] mb-4">{c.descrizione}</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {c.piatti.map((p) => <Badge key={p.id} variant="outline" className="rounded-full text-xs">{p.nome}</Badge>)}
                </div>
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-full">Risparmi {formatEuro(c.risparmio)}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
